import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { Search as SearchIcon, X as ClearIcon } from 'react-feather';
import { TRANSACTION_STYLE_COLORS } from './transactionStyleTokens';

export const TRANSACTION_SEARCH_FIELDS = {
  iot: [
    { key: 'vehicle_number', label: 'Vehicle Number', defaultChecked: true },
    { key: 'vehicle_category', label: 'Vehicle Category' },
    { key: 'address', label: 'Dispenser Address' },
    { key: 'site_name', label: 'Site Name' }
  ],
  inward: [
    { key: 'site_name', label: 'Site Name', defaultChecked: true },
    { key: 'invoice_number', label: 'Invoice Number' },
    { key: 'received_tank', label: 'Received Tank' }
  ],
  transfer: [
    { key: 'vehicle_number', label: 'Vehicle Number', defaultChecked: true },
    { key: 'address', label: 'Dispenser Address' },
    { key: 'site_name', label: 'Site Name' },
    { key: 'card_num_1', label: 'Vehicle Card' }
  ],
  external: [
    { key: 'dispenser', label: 'Dispenser', defaultChecked: true }
  ]
};

const getDefaults = (fields, values = {}) => fields.reduce((result, field) => ({
  ...result,
  [field.key]: Boolean(field.defaultChecked || values[field.key])
}), {});

const getValues = (fields, values = {}) => fields.reduce((result, field) => ({
  ...result,
  [field.key]: values[field.key] || ''
}), {});

const MultiFieldTransactionSearch = ({
  transactionType,
  appliedValues = {},
  onApply,
  onClear,
  sx = {}
}) => {
  const fields = useMemo(
    () => TRANSACTION_SEARCH_FIELDS[transactionType] || [],
    [transactionType]
  );
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => getDefaults(fields, appliedValues));
  const [values, setValues] = useState(() => getValues(fields, appliedValues));
  const [error, setError] = useState('');

  useEffect(() => {
    setSelected(getDefaults(fields, appliedValues));
    setValues(getValues(fields, appliedValues));
    setError('');
  }, [appliedValues, fields]);

  const openSearch = () => {
    setSelected(getDefaults(fields, appliedValues));
    setValues(getValues(fields, appliedValues));
    setError('');
    setOpen(true);
  };

  const handleToggle = (key) => {
    setSelected((current) => ({ ...current, [key]: !current[key] }));
    setError('');
  };

  const handleValueChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setError('');
  };

  const clearField = (key) => {
    setValues((current) => ({ ...current, [key]: '' }));
  };

  const applySearch = () => {
    const populatedValues = fields.reduce((result, field) => {
      const value = selected[field.key] ? values[field.key].trim() : '';
      return value ? { ...result, [field.key]: value } : result;
    }, {});

    if (!Object.keys(populatedValues).length) {
      setError('Enter a value in at least one selected field to search.');
      return;
    }

    onApply(populatedValues);
    setOpen(false);
  };

  const resetAll = () => {
    setSelected(getDefaults(fields));
    setValues(getValues(fields));
    setError('');
    onClear();
    setOpen(false);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<SearchIcon size={16} />}
        onClick={openSearch}
        sx={{
          height: 40,
          width: '100%',
          minWidth: 0,
          justifyContent: 'flex-start',
          textAlign: 'left',
          borderRadius: '8px',
          textTransform: 'none',
          color: TRANSACTION_STYLE_COLORS.primary,
          borderColor: TRANSACTION_STYLE_COLORS.primary,
          backgroundColor: TRANSACTION_STYLE_COLORS.white,
          '&:hover': {
            borderColor: TRANSACTION_STYLE_COLORS.primary,
            backgroundColor: TRANSACTION_STYLE_COLORS.hover
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: 0.75
          },
          ...sx
        }}
      >
        Search
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            width: { xs: 'calc(100% - 16px)', sm: 'auto' },
            m: { xs: 1, sm: 2 },
            borderRadius: 2,
            backgroundColor: TRANSACTION_STYLE_COLORS.white
          }
        }}
      >
        <DialogTitle sx={{ color: TRANSACTION_STYLE_COLORS.foreground, pb: 1 }}>
          Search transactions
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: TRANSACTION_STYLE_COLORS.muted, mb: 2 }}>
            Select one or more fields and enter values. All entered values must match.
          </Typography>
          <Stack spacing={1.5}>
            {fields.map((field) => (
              <Box
                key={field.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(170px, 0.8fr) minmax(0, 1fr)' },
                  alignItems: 'center',
                  gap: { xs: 0.5, sm: 1 },
                  minWidth: 0
                }}
              >
                <FormControlLabel
                  sx={{ minWidth: 0, mr: 0 }}
                  control={(
                    <Checkbox
                      checked={Boolean(selected[field.key])}
                      onChange={() => handleToggle(field.key)}
                      sx={{ color: TRANSACTION_STYLE_COLORS.primary, '&.Mui-checked': { color: TRANSACTION_STYLE_COLORS.primary } }}
                    />
                  )}
                  label={field.label}
                />
                <TextField
                  fullWidth
                  size="small"
                  disabled={!selected[field.key]}
                  value={values[field.key] || ''}
                  placeholder={`Search ${field.label.toLowerCase()}`}
                  onChange={(event) => handleValueChange(field.key, event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') applySearch();
                  }}
                  InputProps={{
                    endAdornment: values[field.key] ? (
                      <IconButton size="small" onClick={() => clearField(field.key)} aria-label={`Clear ${field.label}`}>
                        <ClearIcon size={15} />
                      </IconButton>
                    ) : null
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: TRANSACTION_STYLE_COLORS.foreground,
                      '& fieldset': { borderColor: TRANSACTION_STYLE_COLORS.border },
                      '&:hover fieldset, &.Mui-focused fieldset': { borderColor: TRANSACTION_STYLE_COLORS.primary }
                    }
                  }}
                />
              </Box>
            ))}
          </Stack>
          {error && <Typography role="alert" sx={{ color: '#b42318', mt: 2, fontSize: 14 }}>{error}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={resetAll} sx={{ color: TRANSACTION_STYLE_COLORS.muted, textTransform: 'none' }}>Reset all</Button>
          <Button onClick={() => setOpen(false)} sx={{ color: TRANSACTION_STYLE_COLORS.muted, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={applySearch} variant="contained" sx={{ backgroundColor: TRANSACTION_STYLE_COLORS.primary, textTransform: 'none', '&:hover': { backgroundColor: TRANSACTION_STYLE_COLORS.primary, filter: 'brightness(.95)' } }}>Search</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

MultiFieldTransactionSearch.propTypes = {
  transactionType: PropTypes.oneOf(['iot', 'inward', 'transfer', 'external']).isRequired,
  appliedValues: PropTypes.object,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  sx: PropTypes.object
};

export default MultiFieldTransactionSearch;
